import { Link } from 'react-router-dom';
import { Target, Shield, Zap, Users, BookOpen } from 'lucide-react';

export default function About() {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <span className="text-gradient">About BetMimic</span>
            <Target className="w-7 h-7 text-primary-400" />
          </h1>
          <p className="text-dark-400 mt-2 max-w-2xl text-sm md:text-base">
            BetMimic is a professional copy trading layer built on top of Polymarket. It turns
            high-conviction traders into an automated signal stream for your own wallet, with
            strict risk controls and real PnL pulled directly from Polymarket&apos;s official APIs.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-dark-400">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-dark-800/80 border border-dark-700/70">
            <Shield className="w-3 h-3 text-primary-400" />
            Non-custodial • Your wallet, your USDC.e
          </span>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-dark-800/80 border border-dark-700/70">
            <Zap className="w-3 h-3 text-accent-400" />
            Built on Polymarket CLOB
          </span>
        </div>
      </header>

      {/* What BetMimic does */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="card h-full">
          <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary-400" />
            Copy the traders you already trust
          </h2>
          <p className="text-sm text-dark-400">
            BetMimic watches selected Polymarket wallets in real time. When a tracked trader
            opens or closes a position, your copy config decides how much to mirror, which
            sides to follow, and which markets to skip entirely.
          </p>
        </div>

        <div className="card h-full">
          <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary-400" />
            Risk management first
          </h2>
          <p className="text-sm text-dark-400">
            Every config has hard limits: max exposure, max positions, daily trade and loss
            caps, plus per-position stop loss and take profit. The engine enforces these rules
            before any order ever hits the Polymarket order book.
          </p>
        </div>

        <div className="card h-full">
          <h2 className="text-sm font-semibold mb-2 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary-400" />
            Real PnL, not screenshots
          </h2>
          <p className="text-sm text-dark-400">
            Positions, trades and PnL are reconciled against Polymarket&apos;s official Data API.
            Your Dashboard, Positions tab and trader stats are grounded in the same numbers
            you would see directly on polymarket.com.
          </p>
        </div>
      </section>

      {/* How it works in practice */}
      <section className="card space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2">
          <Users className="w-4 h-4 text-primary-400" />
          How a typical BetMimic setup looks
        </h2>
        <ol className="list-decimal list-inside space-y-1 text-sm text-dark-400">
          <li>Connect your Polymarket trading wallet in Settings → Wallet.</li>
          <li>Fund it with USDC.e on Polygon and confirm you can trade manually.</li>
          <li>Pick one or two proven traders from the Traders page.</li>
          <li>Create a conservative copy config: small fixed size, strict max exposure, stop loss enabled.</li>
          <li>Let the engine mirror new signals into your wallet while you monitor Positions and the Dashboard.</li>
        </ol>
        <p className="text-xs text-dark-500 mt-1">
          You stay in full control: you can pause configs, close positions manually, or tighten
          limits at any time. BetMimic never moves funds out of your own Polymarket wallet.
        </p>
      </section>

      {/* Links */}
      <section className="flex flex-wrap gap-3 text-xs">
        <Link
          to="/tutorial"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800/80 border border-dark-700 text-dark-100 hover:border-primary-500 hover:text-primary-200 transition-colors"
        >
          <BookOpen className="w-4 h-4 text-primary-400" />
          Read the full BetMimic tutorial
        </Link>
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800/80 border border-dark-700 text-dark-100 hover:border-primary-500 hover:text-primary-200 transition-colors"
        >
          <BookOpen className="w-4 h-4 text-primary-400" />
          Visit the Polymarket copy trading blog
        </Link>
      </section>
    </div>
  );
}
